import { useMutation } from '@tanstack/react-query'
import { signUp } from 'aws-amplify/auth'

import { postRequest } from 'src/lib/api-client'
import { sendEvent } from 'src/shared/api/events/send-event'

type SignUpFormData = {
  email: string
  password: string
}

type ResetUserResponse = {
  message: string
  status: string
  onboarding_complete: boolean
}

export const useSignUp = () => {
  return useMutation({
    mutationFn: async ({ email, password }: SignUpFormData) => {
      try {
        const result = await signUp({
          username: email,
          password,
          options: {
            userAttributes: {
              email,
              'custom:onboardingComplete': 'false',
            },
            autoSignIn: { enabled: true },
          },
        })
        sendEvent({ eventType: 'auth_signup_success', info: { email } })
        return result
      } catch (error) {
        if (error instanceof Error && error.name === 'UsernameExistsException') {
          // Try to reset the user if they exist but are in a resettable state
          const resetResponse = (await postRequest<ResetUserResponse>('/auth/reset-user', {
            email,
          })) as ResetUserResponse

          if (resetResponse.status === 'UNCONFIRMED') {
            // Retry signup after successful reset
            const retryResult = await signUp({
              username: email,
              password,
              options: {
                userAttributes: {
                  email,
                  'custom:onboardingComplete': 'false',
                },
                autoSignIn: { enabled: true },
              },
            })
            sendEvent({ eventType: 'error', info: { email, error: 'User signup failed' } })
            return retryResult
          }

          throw error // Re-throw if user couldn't be reset
        }
        throw error
      }
    },
    onError: (error: unknown) => {
      const errorName = error instanceof Error ? error.name : 'unknown'
      sendEvent({
        eventType: 'auth_signup_error',
        info: { error: errorName },
      })
    },
  })
}
